import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  FormLabel,
  Grid,
  SxProps,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material";
import useFormStyles from "../../../styles/useFormStyles";
import { inputStateUpdate } from "../utility";
import theme from "../../../theme";

export type MultipleSelectInputFieldProps = {
  label?: string;
  options: string[];
  align: string;
  setState: Function;
  stateKey: string;
  defaultValue: string;
  sx?: SxProps;
  clear?: boolean;
  setClear?: Function;
  required?: boolean;
  multiple?: boolean;
  selectedText?: string;
  readOnly?: boolean;
};

const MultipleSelectInputField: React.FC<MultipleSelectInputFieldProps> = (
  props: MultipleSelectInputFieldProps
) => {
  const {
    label,
    options,
    align,
    setState,
    stateKey,
    defaultValue,
    sx,
    clear,
    setClear,
    required,
    selectedText,
    readOnly, 
  } = props;

  const [selectState, setSelectState] = useState<string[]>(
    defaultValue ? defaultValue.split(",") : []
  );
  const formStyles = useFormStyles();

  const changeHandler = (e: SelectChangeEvent<unknown>) => {
    const selectedOptions = e.target.value as string[];
    if (setClear instanceof Function) setClear(false);
    setSelectState(selectedOptions);
  };

  useEffect(() => {
    inputStateUpdate(stateKey, setState, selectState.toString());
  }, [selectState]);

  useEffect(() => {
    if (clear) {
      setSelectState([]);
    }
  }, [clear]);

  const selectedItemsCount = selectState.length;
  const maxDisplayedItems = 2;
  const remainingSelectedCount = selectedItemsCount - maxDisplayedItems;

  const readOnlyStyles: React.CSSProperties = {
    backgroundColor: theme.color?.readOnly,
  };

  return (
    <Grid>
      <FormControl size="small" sx={{ width: "100%" }}>
        {label ? (
          <FormLabel sx={{ ...formStyles.formLabel, color: "#252836" }}>{label} </FormLabel>
        ) : null}
        <Select
          multiple
          value={selectState}
          onChange={changeHandler}
          disabled={readOnly} 
          renderValue={(selected) => {
            const selectedValues = selected as string[];
            if (selectedValues.length <= maxDisplayedItems) {
              return selectedValues.join(", ");
            } else {
              return (
                <>
                  {selectedValues.slice(0, maxDisplayedItems).join(", ")}{" "}
                  .. +{remainingSelectedCount} Selected
                </>
              );
            }
          }}
        >
          {options.map((option, i) => {
            const isSelected = selectState.includes(option);
            return (
              <MenuItem
                key={i}
                value={option}
                sx={{
                  backgroundColor: selectState.includes(option) ? theme.color?.primaryMedium : "initial",
                  "&.Mui-selected": {
                    backgroundColor: theme.color?.primaryMedium,
                  },
                }}
              >
                <Typography textAlign={"left"} variant="body1" >
                  {option}
                </Typography>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Grid>
  );
};

export default MultipleSelectInputField;
